import React from 'react';
import { useRef } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import { getLanguage } from 'translation';
import SocialMedia from 'pages/Company/components/SocialMedia';
import Breadcrumbs from 'components/BreadCrumbs';
import LazyLoadImage from 'components/LazyLoadImage';
import StyledList from 'components/StyledList';
import CopyUrlButton from 'components/CopyUrlButton';
import ConsultantPayment from 'components/ConsultantPayment';
import getYearsLabel from 'helpers/getYearsLabel';
import PlusIcon from 'assets/icons/icon-plus-box-outline.svg?react';
import LocationIcon from 'assets/icons/location_c.svg?react';
import VerifiedIcon from 'assets/icons/verified-user.svg?react';
import LanguagesIcon from 'assets/icons/languages.svg?react';
import MedalIcon from 'assets/icons/medal.svg?react';
import ConsultantIcon from 'assets/images/consultant-default.png';
import MentorIcon from 'assets/images/mentor-default.png';

const Background = styled('section')(({ theme, isMobile }) => ({
  backgroundColor: theme.palette.white.main,
  paddingBottom: isMobile ? '100px' : '40px' // Reduced padding to minimize black space
}));

const Wrapper = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  maxWidth: 1240,
  display: 'flex',
  gap: 120,
  flexDirection: 'row-reverse',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30,
    gap: 20,
    flexDirection: 'column'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 20px',
    paddingTop: 20,
    paddingBottom: 10,
    gap: 0,
    display: 'block'
  }
}));

const RightSidebarWrapper = styled('section')(({ theme }) => ({
  height: 'fit-content',
  position: 'relative',
  minHeight: 'calc(var(--LeftSidebarHeight, 600px))', // Fallback height for desktop sticky behavior
  width: 285, // Fixed width to maintain layout space
  flexShrink: 0, // Prevent shrinking
  [theme.breakpoints.down('md')]: {
    width: 320, // Make wider on mobile/tablet
    margin: '0 auto', // Center the container
    minHeight: 'auto' // Disable equal-height behavior on mobile to remove huge gap
  },
  [theme.breakpoints.down('sm')]: {
    width: '90%', // Even wider on small mobile
    maxWidth: 400
  }
}));

const RightSidebar = styled('div')(({ theme }) => ({
  maxWidth: 285,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    width: '100%'
  }
}));

const LeftSidebarWrapper = styled('section')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

const LeftSidebar = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 35,
  [theme.breakpoints.down('md')]: {
    fontSize: 28,
    lineHeight: '32px',
    fontWeight: 600,
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none' // Hide desktop title on small screens, use mobile title instead
  }
}));

const TitleMobile = styled(Typography)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 16
  }
}));

const HeadLine = styled(Typography)(({ theme }) => ({
  marginTop: 30,
  marginBottom: 20,
  [theme.breakpoints.down('md')]: {
    fontSize: 26,
    lineHeight: '30px',
    marginTop: 24,
    marginBottom: 16
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 22,
    lineHeight: '28px',
    marginTop: 20,
    marginBottom: 14
  }
}));

const PlusIconStyled = styled(PlusIcon)(({ theme }) => ({
  width: 24,
  height: 24,
  flexShrink: 0,
  marginRight: 12,
  [theme.breakpoints.down('md')]: {
    width: 20,
    height: 20,
    marginRight: 10
  }
}));

const BenefitTitle = styled(Typography)(({ theme }) => ({
  fontSize: 21,
  lineHeight: '24px',
  color: theme.palette.textPrimary.main,
  marginTop: 0,
  marginBottom: 10,
  fontWeight: 600,
  flex: 1,
  paddingTop: 2,
  [theme.breakpoints.down('md')]: {
    fontSize: 22,
    lineHeight: '26px',
    fontWeight: 600,
    paddingTop: 0
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    lineHeight: '24px'
  }
}));

const BenefitDescription = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  lineHeight: '27px',
  opacity: 0.8,
  color: theme.palette.textPrimary.main,
  marginBottom: 30,
  [theme.breakpoints.down('md')]: {
    fontSize: 18,
    lineHeight: '27px',
    fontWeight: 400,
    marginBottom: 25
  }
}));

const SocialWrapper = styled('div')(({ theme }) => ({
  marginBottom: 30,
  display: 'flex',
  gap: 20,
  justifyContent: 'flex-start',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    marginBottom: 20
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: 10,
  marginBottom: 20,
  borderTop: '3px solid rgba(121, 201, 18, 1)',
  [theme.breakpoints.down('md')]: {
    marginTop: 15,
    marginBottom: 20
  }
}));

const AdvantagesWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 25,
  '& > div': {
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: 0,
    '& > div': {
      marginBottom: 25
    }
  }
}));

const UserImage = styled('div')(({ theme }) => ({
  width: 285,
  height: 285,
  borderRadius: 40,
  overflow: 'hidden',
  marginBottom: 25,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  '& svg': {
    width: '100%',
    height: '100%',
    color: theme.palette.grey[300]
  },
  [theme.breakpoints.down('md')]: {
    width: 160,
    height: 160,
    margin: '0 auto',
    marginBottom: 20,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    }
  }
}));

const UserName = styled(Typography)(({ theme }) => ({
  marginBottom: 20,
  [theme.breakpoints.down('md')]: {
    fontSize: 28,
    lineHeight: '27px',
    fontWeight: 600
  }
}));

const TemplateText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  marginBottom: 20,
  '& svg': {
    width: 35,
    marginRight: 10,
    flex: 1
  },
  '& span': {
    flex: 8
  }
}));

const ShortInfo = styled(AdvantagesWrapper)(({ theme }) => ({
  gap: 0,
  marginBottom: 35,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 0
  }
}));


const HiddenSm = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const ShownSm = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block'
  }
}));

const IframeWrapper = styled('div')(({ theme }) => ({
  marginBottom: 30,
  '& iframe': {
    width: '100%',
    height: 460,
    borderRadius: 40,
    [theme.breakpoints.down('md')]: {
      height: 180
    }
  }
}));

const ConsultantInformation = ({ info, endPoint }) => {
  const [lang] = React.useState(getLanguage().toUpperCase());
  const t = useTranslate('Consultant');
  const isMobile = useSelector((state) => state.app.isMobile);

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: endPoint || 'Consultants',
        link: `/${endPoint || 'consultants'}`
      },
      {
        label: 'Consultant'
      }
    ];
  }, [endPoint]);

  // On mobile, show only a single breadcrumb that goes back to the list page
  const mobileBreadcrumbs = React.useMemo(() => {
    return [
      {
        label: endPoint || 'Consultants',
        link: `/${endPoint || 'consultants'}`
      }
    ];
  }, [endPoint]);

  const userName = React.useMemo(
    () => `${info[`firstName${lang}`]} ${info[`lastName${lang}`]}`,
    [lang, info]
  );

  const consultWithTitle = React.useMemo(
    () =>
      t('ConsultateWith', {
        name:
          info[`instrumentalDeclension${lang}`]?.firstName +
          ' ' +
          info[`instrumentalDeclension${lang}`]?.lastName
      }),
    [t, info, lang]
  );

  const socialMedia = React.useMemo(() => {
    const list = [];

    Object.keys(info?.socialMediaLinks || {}).forEach((key) => {
      if (info?.socialMediaLinks[key]) {
        list.push({
          website: info?.socialMediaLinks[key],
          id: info?.socialMediaLinks[key]
        });
      }
    });

    return list;
  }, [info]);

  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);

  React.useEffect(() => {
    if (!leftSidebarRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (leftSidebarRef.current) {
        const leftSidebarHeight = leftSidebarRef.current.offsetHeight;
        document.documentElement.style.setProperty('--LeftSidebarHeight', `${leftSidebarHeight}px`);
      }
    });

    resizeObserver.observe(leftSidebarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Handle sticky sidebar behavior (only on desktop)
  React.useEffect(() => {
    if (!rightSidebarRef.current || isMobile) return;

    let originalRect = null;
    let isInitialized = false;

    const initializeSidebar = () => {
      if (!rightSidebarRef.current || isInitialized) return;

      const sidebar = rightSidebarRef.current;
      originalRect = sidebar.getBoundingClientRect();

      // Make sidebar sticky immediately
      sidebar.style.position = 'fixed';
      sidebar.style.top = '115px';
      sidebar.style.left = `${originalRect.left}px`;
      sidebar.style.width = `${originalRect.width}px`;

      isInitialized = true;
    };

    const handleScroll = () => {
      if (!isInitialized) {
        initializeSidebar();
      }

      const sidebar = rightSidebarRef.current;
      const reviewsSection = document.querySelector('[data-reviews-section]');
      const scrollY = window.scrollY;

      if (reviewsSection && originalRect) {
        const reviewsTop = reviewsSection.offsetTop;
        const windowHeight = window.innerHeight;

        // Calculate where the sidebar should stop being fixed
        // Stop when the reviews section starts coming into view
        const stopPosition = reviewsTop - windowHeight; // Stop when reviews section reaches the bottom of viewport

        if (scrollY >= stopPosition) {
          // Sidebar should become scrollable - position it absolutely at its current viewport position
          // This keeps it visible and allows it to scroll with the content
          sidebar.style.position = 'absolute';
          sidebar.style.top = `${scrollY + 115}px`; // Current viewport position in document coordinates
          sidebar.style.left = `${originalRect.left}px`;
          sidebar.style.width = `${originalRect.width}px`;
        } else {
          // Ensure sidebar stays fixed
          sidebar.style.position = 'fixed';
          sidebar.style.top = '115px';
          sidebar.style.left = `${originalRect.left}px`;
          sidebar.style.width = `${originalRect.width}px`;
        }
      }
    };

    // Initialize immediately
    initializeSidebar();

    // Also try again after a short delay to ensure DOM is ready
    const initTimer = setTimeout(initializeSidebar, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);


  const userImage = info?.photoLinks?.main
    ? info?.photoLinks?.main + '/w285h325'
    : endPoint === 'consultants'
      ? ConsultantIcon
      : MentorIcon;

  // Mobile sticky button component
  const MobileStickyButton = () => (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'white',
      padding: '16px 20px',
      borderTop: '1px solid #e0e0e0',
      zIndex: 1000,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
    }}>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => {
          // Trigger the main consultation button inside ConsultantPayment
          const ctaButton = document.querySelector('[data-cta="get-consultation"]');
          if (ctaButton) {
            ctaButton.click();
          } else if (rightSidebarRef.current) {
            // Fallback: scroll to the payment section
            rightSidebarRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        sx={{
          // 8px in pixels — bypasses the MUI sx shape multiplier which
          // would otherwise render borderRadius: 3 as 12 px (3 × 4).
          borderRadius: '8px',
          padding: '12px',
          fontSize: '16px',
          fontWeight: 600
        }}
      >
        Замовити консультацію
      </Button>
    </div>
  );

  if (!info) return null;

  return (
    <Background isMobile={isMobile}>
      <Wrapper>
        <ShownSm>
          <Breadcrumbs breadcrumbs={mobileBreadcrumbs} />
        </ShownSm>
        {isMobile && (
          <Box sx={{ px: { xs: 2.5, sm: 0 }, mt: 2 }}>
            <TitleMobile variant="h1">{consultWithTitle}</TitleMobile>
          </Box>
        )}
        <RightSidebarWrapper>
          <RightSidebar ref={rightSidebarRef}>
            <UserImage>
              <LazyLoadImage alt={userName} src={userImage} />
            </UserImage>

            <ConsultantPayment info={info} />
          </RightSidebar>
        </RightSidebarWrapper>
        <LeftSidebarWrapper>
          <LeftSidebar ref={leftSidebarRef}>
            <HiddenSm>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </HiddenSm>

            <Title variant="h1">
              {consultWithTitle}
            </Title>

            <ShortInfo>
              <TemplateText>
                <VerifiedIcon />
                <span>{t('VerifiedUser')}</span>
              </TemplateText>

              <TemplateText>
                <MedalIcon />
                <span>
                  {t('YearsExp', {
                    count: `${info?.experience} ${t(getYearsLabel(info?.experience))}`
                  })}
                </span>
              </TemplateText>

              <TemplateText>
                <LanguagesIcon />
                <span>{t('SessionsCount', { count: info?.sessionCount })}</span>
              </TemplateText>

              <TemplateText>
                <LanguagesIcon />
                <span>{info?.languages?.map((lang) => lang).join(', ')}</span>
              </TemplateText>

              <TemplateText>
                <LocationIcon />
                <span>
                  {info?.isWorkOffline ? (
                    <>
                      {info?.location}
                      {t('Or')}
                    </>
                  ) : null}
                  {t('Online')}
                </span>
              </TemplateText>
            </ShortInfo>

            <UserName variant={'h3'}>{userName}</UserName>

            <StyledList list={info[`summary${lang}`]} variant={'arrow'} />

            <SocialWrapper>
              <SocialMedia list={socialMedia} />
              <CopyUrlButton authRequired={false} />
            </SocialWrapper>

            {info?.videoLinks?.main ? (
              <IframeWrapper>
                <iframe
                  src={`${info?.videoLinks?.main}`}
                  title={userName}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </IframeWrapper>
            ) : null}

            <HeadLine variant="h2">{t('ConsulationsOptions')}</HeadLine>

            <AdvantagesWrapper>
              {(info?.sessionContent || []).map((item) => (
                <Box key={item._id}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: 1 }}>
                    <Box sx={{ paddingTop: '3px', flexShrink: 0 }}>
                      <PlusIconStyled />
                    </Box>
                    <BenefitTitle variant="h3">{item[`name${lang}`]}</BenefitTitle>
                  </Box>
                  <BenefitDescription variant="body1">
                    {item[`description${lang}`]}
                  </BenefitDescription>
                </Box>
              ))}
            </AdvantagesWrapper>

            <StyledDivider />

            <HeadLine variant="h2">
              {t('ConsultationsSpecialOptions', {
                name: info[`instrumentalDeclension${lang}`]?.firstName
              })}
            </HeadLine>

            <StyledList list={info[`advantages${lang}`]} />
          </LeftSidebar>
        </LeftSidebarWrapper>
      </Wrapper>

      {/* Mobile sticky button */}
      {isMobile && <MobileStickyButton />}
    </Background>
  );
};

export default ConsultantInformation;
